import React, { useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import {
  KButton,
  KCheckbox,
  KIcon,
  KSelectAutocomplete,
  KTable,
} from "@kyburz/kdesign-system";

import update from "immutability-helper";
import useSetState from "../../../hooks/useSetState";
import { TemplateParameter } from "../facades/TemplateParameter";
import { ChangableVehicleTemplate } from "../facades/VehicleTemplate";

import Editable from "../../../components/ui/Editable";

const useStyles = makeStyles(() => ({
  tableContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
  },
  parameterInput: {
    display: "inline-flex",
    "& > div > div": {
      marginTop: "0 !important",
    },
  },
  newParameterForm: {
    display: "flex",
    gap: 16,
    alignItems: "flex-end",
    justifyContent: "flex-start",
  },
}));

type VehicleTemplateListProps = Omit<ChangableVehicleTemplate, "companies"> & {
  availableParameters: TemplateParameter[];
  onTemplateChanged: (template: Partial<ChangableVehicleTemplate>) => void;
};

const MAX_DISPLAY_FIELDS = 6;

const VehicleTemplateList: React.FC<VehicleTemplateListProps> = ({
  availableParameters = [],
  onTemplateChanged = () => {},
  ...template
}) => {
  const classes = useStyles();

  const [{ selectedParameter }, setState] = useSetState({
    selectedParameter: "",
  });

  const handleAddColumn = (parameter: string) => {
    if (parameter !== "") {
      const newColumn = {
        technicalName: parameter,
        isDefault: false,
        isDigital: false,
        default: "-",
        name: { en: "", de: "", fr: "", it: "" },
        hasHover: false,
        hover: { en: "", de: "", fr: "", it: "" },
        unit: "-",
        scale_factor: 0,
        digits_after_comma: 0,
        values_mapping_name: null,
        type: null,
      };

      onTemplateChanged({ columns: [...template.columns, newColumn] });
      setState({ selectedParameter: "" });
    }
  };

  const handleSelect = (_, el) => setState({ selectedParameter: el.label });

  const parameters = template.columns;

  const updateColumn = useCallback(
    (row: any, spec: any) => {
      const newRow = update(row.row.original, spec);
      const newRows = [...row.data];
      newRows[row.row.index] = newRow;

      onTemplateChanged({ columns: newRows });
    },
    [onTemplateChanged],
  );

  const cachedAvailableParameters = useMemo(
    () =>
      availableParameters
        .filter(
          ({ parameter }) =>
            !template.columns.find(
              ({ technicalName }) => parameter === technicalName,
            ),
        )
        .map((el) => ({ label: el.parameter, value: el.id })),
    [availableParameters, template.columns],
  );

  const handleDisplayColumn = (e, technicalName: string) => {
    let displayColumns = [...(template.displayColumns || [])];
    if (e.target.checked) {
      displayColumns.push(technicalName);
    } else {
      displayColumns = displayColumns.filter(
        (c: string) => c !== technicalName,
      );
    }
    onTemplateChanged({ displayColumns });
  };

  const renderDisplayCheckbox = (row) => {
    const field = row.row.original.technicalName;
    const checked = template?.displayColumns?.includes(field);
    const max = (template?.displayColumns?.length || 0) >= MAX_DISPLAY_FIELDS;
    return (
      <KCheckbox
        name="isDisplay"
        checked={checked}
        style={{ marginTop: 0 }}
        handleChange={(e) => handleDisplayColumn(e, field)}
        disabled={!checked && max}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: "Technical Name",
        accessor: "technicalName",
        width: 240,
      },
      {
        id: "display",
        Header: `Display ${template.displayColumns?.length || 0}/6`,
        Cell: renderDisplayCheckbox,
        width: 90,
      },
      {
        id: "isDefault",
        Header: "Is Default?",
        Cell: (row) => (
          <KCheckbox
            name="isDefault"
            checked={row.row.original.isDefault}
            style={{ marginTop: 0 }}
            handleChange={(e) =>
              updateColumn(row, { isDefault: { $set: e.target.checked } })
            }
          />
        ),
        width: 90,
      },
      {
        id: "isDigital",
        Header: "Is Digital?",
        Cell: (row) => {
          return (
            <KCheckbox
              name="isDigital"
              checked={row.row.original.isDigital}
              style={{ marginTop: 0 }}
              handleChange={(e) =>
                updateColumn(row, { isDigital: { $set: e.target.checked } })
              }
            />
          );
        },
        width: 90,
      },
      {
        Header: "Default Value",
        accessor: "default",
        Cell: (row) => (
          <Editable
            text={row.row.original.default}
            onChange={(val) => updateColumn(row, { default: { $set: val } })}
          />
        ),
        width: 110,
      },
      {
        Header: "Column Name",
        columns: [
          {
            id: "NameEnglish",
            Header: "English",
            Cell: (row) => (
              <Editable
                text={row.row.original.name.en}
                onChange={(val) =>
                  updateColumn(row, { name: { en: { $set: val } } })
                }
              />
            ),
          },
          {
            id: "NameGerman",
            Header: "German",
            Cell: (row) => (
              <Editable
                text={row.row.original.name.de}
                onChange={(val) =>
                  updateColumn(row, { name: { de: { $set: val } } })
                }
              />
            ),
          },
          {
            id: "NameFrench",
            Header: "French",
            Cell: (row) => (
              <Editable
                text={row.row.original.name.fr}
                onChange={(val) =>
                  updateColumn(row, { name: { fr: { $set: val } } })
                }
              />
            ),
          },
          {
            id: "NameItalian",
            Header: "Italian",
            Cell: (row) => (
              <Editable
                text={row.row.original.name.it}
                onChange={(val) =>
                  updateColumn(row, { name: { it: { $set: val } } })
                }
              />
            ),
          },
        ],
      },
      {
        id: "hasHover",
        Header: "Hover?",
        Cell: (row) => (
          <KCheckbox
            name="hasHover"
            checked={row.row.original.hasHover}
            style={{ marginTop: 0 }}
            handleChange={(e) =>
              updateColumn(row, { hasHover: { $set: e.target.checked } })
            }
          />
        ),
        width: 80,
      },
      {
        Header: "Hover Text",
        columns: [
          {
            id: "HoverEnglish",
            Header: "English",
            Cell: (row) => (
              <Editable
                text={row.row.original.hover.en}
                onChange={(val) =>
                  updateColumn(row, { hover: { en: { $set: val } } })
                }
              />
            ),
          },
          {
            id: "HoverGerman",
            Header: "German",
            Cell: (row) => (
              <Editable
                text={row.row.original.hover.de}
                onChange={(val) =>
                  updateColumn(row, { hover: { de: { $set: val } } })
                }
              />
            ),
          },
          {
            id: "HoverFrench",
            Header: "French",
            Cell: (row) => (
              <Editable
                text={row.row.original.hover.fr}
                onChange={(val) =>
                  updateColumn(row, { hover: { fr: { $set: val } } })
                }
              />
            ),
          },
          {
            id: "HoverItalian",
            Header: "Italian",
            Cell: (row) => (
              <Editable
                text={row.row.original.hover.it}
                onChange={(val) =>
                  updateColumn(row, { hover: { it: { $set: val } } })
                }
              />
            ),
          },
        ],
      },
      {
        id: "actions",
        Header: "Actions",
        Cell: (row) => (
          <div
            style={{ display: "inline-block", cursor: "pointer" }}
            onClick={() => {
              const newRows = row.data.filter(
                (el, index) => index !== row.row.index,
              );
              onTemplateChanged({ columns: newRows });
            }}
          >
            <KIcon icon="trash-2" color="#383F51" size={20} />
          </div>
        ),
        width: 60,
      },
    ],
    [onTemplateChanged, updateColumn],
  );

  return (
    <>
      <div className={classes.newParameterForm}>
        <KSelectAutocomplete
          elements={cachedAvailableParameters}
          value={selectedParameter}
          onChange={handleSelect}
          size="small"
          name="selectedParameter"
          placeholder="Parameter"
          style={{ marginTop: 0 }}
        />

        <KButton
          children="Add"
          theme="primary"
          disabled={selectedParameter === ""}
          onClick={() => handleAddColumn(selectedParameter)}
          size="small"
        />
      </div>

      <div className={classes.tableContainer}>
        <KTable data={parameters} columns={columns} />
      </div>
    </>
  );
};

export default VehicleTemplateList;
